import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Check, Clock, Compass, Heart, Loader2, Rocket, Sparkles, Sunrise, ThumbsUp, TrendingUp, Target,
} from "lucide-react";
import { TopicMasteryBadge, TopicNationalStanding } from "@/components/report/MoatCards";
import type { InterestOption } from "@/lib/studentCourses";

interface TopicRow {
  subject: string;
  topic: string;
  mastery: number;
  percentile: number;
}

interface LearningStyle {
  avgResponseSeconds: number | null;
  preferredTime: "morning" | "afternoon" | "evening" | "night" | null;
  avgSessionMinutes: number | null;
  sessionsPerWeek: number | null;
  lessonShare: number | null;
}

interface LearnerProfilePayload {
  profile: {
    userId: string;
    interests: string[];
    goals: string | null;
    stage: "primary" | "secondary" | "beyond";
    learningStyle: LearningStyle | null;
    strengths: TopicRow[];
    weaknesses: TopicRow[];
    ability: { theta: number; percentile: number; se: number; responses: number; source: string } | null;
    trend: "accelerating" | "steady" | "slipping" | "new";
    updatedAt: string;
  };
  snapshots: Array<{
    takenAt: string;
    averageMastery: number;
    averagePercentile: number;
    abilityPercentile: number;
    topicsMastered: number;
    topicsPractised: number;
    topics: Array<{ subject: string; topicKey: string; topic: string; mastery: number; percentile: number; band: string }>;
  }>;
  interestOptions: InterestOption[];
}

const TREND_META: Record<string, { label: string; blurb: string; className: string }> = {
  accelerating: {
    label: "Speeding up",
    blurb: "You are learning faster than before. Keep that energy going!",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  },
  steady: {
    label: "Nice and steady",
    blurb: "You are keeping a good pace. Steady practice wins.",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  slipping: {
    label: "Taking it slower",
    blurb: "Things have slowed a little, and that is okay. A short practice today puts you right back on track.",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  new: {
    label: "Just getting started",
    blurb: "Your journey is only beginning. Every question you answer teaches us how to help you.",
    className: "bg-muted text-muted-foreground",
  },
};

const TIME_OF_DAY: Record<string, { label: string; emoji: string }> = {
  morning: { label: "Mornings", emoji: "🌅" },
  afternoon: { label: "Afternoons", emoji: "☀️" },
  evening: { label: "Evenings", emoji: "🌆" },
  night: { label: "Night time", emoji: "🌙" },
};

const MASTERY_COLOR = "#9b87f5";
const PERCENTILE_COLOR = "#06b6d4";
const GOALS_LIMIT = 280;

export function LearnerProfile() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery<LearnerProfilePayload>({
    queryKey: ["/api/profile/learner"],
  });

  const [interests, setInterests] = useState<string[]>([]);
  const [goals, setGoals] = useState("");
  const [touched, setTouched] = useState(false);

  // Load the saved values once they arrive, unless the student has already started editing.
  useEffect(() => {
    if (!data || touched) return;
    setInterests(data.profile?.interests ?? []);
    setGoals(data.profile?.goals ?? "");
  }, [data, touched]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/profile/learner", {
        interests,
        goals: goals.slice(0, GOALS_LIMIT),
      });
      return res.json();
    },
    onSuccess: () => {
      setTouched(false);
      queryClient.invalidateQueries({ queryKey: ["/api/profile/learner"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recommendations"] });
      toast({ title: "Saved!", description: "We will use this to pick courses you will love." });
    },
    onError: () => toast({ title: "Could not save that", description: "Check your connection and try again.", variant: "destructive" }),
  });

  const chartData = useMemo(() => {
    return (data?.snapshots ?? []).map(snapshot => ({
      date: new Date(snapshot.takenAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      mastery: Math.round(snapshot.averageMastery ?? 0),
      standing: Math.round(snapshot.abilityPercentile ?? 0),
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="skeleton-learner-profile">
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  if (error || !data?.profile) {
    return (
      <Card>
        <CardContent className="p-8 text-center space-y-3">
          <p className="text-sm text-muted-foreground" data-testid="text-learner-error">
            We could not load your learning profile just now.
          </p>
          <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/profile/learner"] })}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const profile = data.profile;
  const interestOptions = data.interestOptions ?? [];
  const trend = TREND_META[profile.trend] ?? TREND_META.new;
  const style = profile.learningStyle;
  const hasStyle = !!style && (!!style.preferredTime || !!style.avgSessionMinutes || !!style.sessionsPerWeek || style.lessonShare != null);
  const dirty =
    touched &&
    (goals !== (profile.goals ?? "") ||
      interests.length !== (profile.interests ?? []).length ||
      interests.some(id => !(profile.interests ?? []).includes(id)));

  const toggleInterest = (id: string) => {
    setTouched(true);
    setInterests(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  return (
    <div className="space-y-4">
      {/* ── Interests + goals ── */}
      <Card data-testid="card-interests">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="h-4 w-4 text-primary" />
            What you love
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Pick as many as you like. We use these to find courses made for you.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {interestOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Interest topics are on the way. Check back soon!</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {interestOptions.map(option => {
                const selected = interests.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleInterest(option.id)}
                    aria-pressed={selected}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-semibold border-2 transition-colors min-h-[36px] ${
                      selected ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"
                    }`}
                    data-testid={`chip-interest-${option.id}`}
                  >
                    <span className="mr-1">{option.emoji}</span>
                    {option.label}
                    {selected && <Check className="inline h-3.5 w-3.5 ml-1" />}
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="learner-goals" className="text-sm font-semibold flex items-center gap-1.5">
              <Target className="h-4 w-4 text-primary" /> What do you want to get better at?
            </label>
            <Textarea
              id="learner-goals"
              value={goals}
              onChange={e => { setTouched(true); setGoals(e.target.value.slice(0, GOALS_LIMIT)); }}
              placeholder="Example: I want to be faster at fractions and read longer stories."
              className="text-sm resize-none h-20"
              data-testid="input-learner-goals"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Your teacher tools use this to help you.</span>
              <span className="text-xs text-muted-foreground">{goals.length}/{GOALS_LIMIT}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
              data-testid="button-save-learner"
            >
              {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
              Save
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setLocation("/courses")} data-testid="button-learner-browse-courses">
              <Compass className="h-3.5 w-3.5 mr-1.5" /> Browse courses
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Trajectory ── */}
      <Card data-testid="card-journey">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Your journey
              </CardTitle>
              <p className="text-xs text-muted-foreground">How your mastery and your national standing have moved over time.</p>
            </div>
            <Badge className={`border-0 ${trend.className}`} data-testid="badge-trend">{trend.label}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">{trend.blurb}</p>
          {chartData.length < 2 ? (
            <div className="bg-muted/30 rounded-lg p-8 text-center" data-testid="text-journey-empty">
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Your journey starts with your first quiz</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setLocation("/learning")} data-testid="button-journey-start">
                Practise now
              </Button>
            </div>
          ) : (
            <div className="w-full" style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" className="text-xs" tickLine={false} />
                  <YAxis domain={[0, 100]} className="text-xs" tickLine={false} />
                  <Tooltip formatter={(value: number, name: string) => [`${value}%`, name]} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Line type="monotone" dataKey="mastery" name="Mastery" stroke={MASTERY_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="standing" name="National standing" stroke={PERCENTILE_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Strengths and weaknesses ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TopicList
          title="What you are great at"
          icon={<ThumbsUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
          rows={profile.strengths ?? []}
          emptyText="Answer a few more questions and your strongest topics will show up here."
          testId="strengths"
        />
        <TopicList
          title="What to work on next"
          icon={<Rocket className="h-4 w-4 text-primary" />}
          rows={profile.weaknesses ?? []}
          emptyText="Nothing flagged yet. Keep practising and we will point out what needs work."
          testId="weaknesses"
        />
      </div>

      {/* ── How you learn ── */}
      <Card data-testid="card-learning-style">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sunrise className="h-4 w-4 text-primary" />
            How you learn
          </CardTitle>
          <p className="text-xs text-muted-foreground">Built from when and how you have been studying.</p>
        </CardHeader>
        <CardContent>
          {!hasStyle ? (
            <div className="bg-muted/30 rounded-lg p-6 text-center" data-testid="text-style-empty">
              <Clock className="h-7 w-7 mx-auto mb-2 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                Study a few more times and we will show you your own learning habits here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StyleTile
                label="Best time of day"
                value={style?.preferredTime ? `${TIME_OF_DAY[style.preferredTime]?.emoji ?? ""} ${TIME_OF_DAY[style.preferredTime]?.label ?? style.preferredTime}` : "Not sure yet"}
                testId="tile-preferred-time"
              />
              <StyleTile
                label="Usual session"
                value={style?.avgSessionMinutes ? `${Math.round(style.avgSessionMinutes)} min` : "Not sure yet"}
                testId="tile-session-length"
              />
              <StyleTile
                label="Times a week"
                value={style?.sessionsPerWeek ? `${Math.round(style.sessionsPerWeek)}` : "Not sure yet"}
                testId="tile-sessions-week"
              />
              <StyleTile
                label="Lessons vs quizzes"
                value={
                  style?.lessonShare === null || style?.lessonShare === undefined
                    ? "Not sure yet"
                    : `${Math.round(style.lessonShare * 100)}% lessons`
                }
                testId="tile-lesson-share"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StyleTile({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="rounded-lg bg-muted/50 p-3 text-center" data-testid={testId}>
      <p className="text-sm font-bold leading-tight">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function TopicList({
  title,
  icon,
  rows,
  emptyText,
  testId,
}: {
  title: string;
  icon: React.ReactNode;
  rows: TopicRow[];
  emptyText: string;
  testId: string;
}) {
  return (
    <Card data-testid={`card-${testId}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">{icon}{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid={`text-${testId}-empty`}>{emptyText}</p>
        ) : (
          <div className="space-y-2">
            {rows.map(row => (
              <div
                key={`${row.subject}-${row.topic}`}
                className="flex items-start gap-3 p-2.5 rounded-md bg-muted/40 flex-wrap"
                data-testid={`row-${testId}-${row.topic}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{row.topic}</p>
                  <p className="text-xs text-muted-foreground capitalize">{row.subject}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <TopicMasteryBadge mastery={row.mastery} band={null} />
                  <TopicNationalStanding percentile={row.percentile} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LearnerProfile;
